import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { expandAllowedSectionSet } from '../utils/adminNavConfig';
import { DEFAULT_TEAM_STATS_TEAM_ID, TEAM_STATS_TEAMS } from '../utils/teamStatsConfig';
import HockeyStatTrackerInner from './HockeyStatTrackerInner';

const STAT_TRACKER_TEAM_STORAGE_KEY = 'gcg-stat-tracker-active-team';

function readStoredStatTrackerTeamId(fallbackTeamId) {
  const allowed = new Set(TEAM_STATS_TEAMS.map((t) => t.teamId));
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STAT_TRACKER_TEAM_STORAGE_KEY);
    if (stored && allowed.has(stored)) return stored;
  }
  if (fallbackTeamId && allowed.has(fallbackTeamId)) return fallbackTeamId;
  return DEFAULT_TEAM_STATS_TEAM_ID;
}

function canUseStatTracker(allowedSet) {
  return (
    allowedSet.has('liveScoreboard') ||
    allowedSet.has('gameHistory') ||
    allowedSet.has('gladiatorsTv')
  );
}

export default function HockeyStatTracker(props) {
  const [gate, setGate] = useState('loading');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [signedInAs, setSignedInAs] = useState('');
  const [activeTeamId, setActiveTeamId] = useState(() =>
    readStoredStatTrackerTeamId(props.teamId)
  );

  const setTeamAndPersist = (teamId) => {
    setActiveTeamId(teamId);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STAT_TRACKER_TEAM_STORAGE_KEY, teamId);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setSignedInAs('');
        setGate('login');
        return;
      }
      setGate('loading');
      setSignedInAs(user.email || user.uid);
      try {
        const snap = await getDoc(doc(db, 'adminUsers', user.uid));
        if (!snap.exists() || snap.data()?.active === false) {
          // Do NOT sign out here — that logs parents out of the whole site and causes auth/Firestore flicker.
          setGate('denied');
          return;
        }
        const allowed = expandAllowedSectionSet(snap.data()?.sections);
        if (!canUseStatTracker(allowed)) {
          setGate('denied');
          return;
        }
        setGate('ready');
      } catch (e) {
        console.error(e);
        setGate('denied');
      }
    });
    return () => unsub();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      // If a parent (or other) session is active, sign out first so staff login can succeed.
      if (auth.currentUser) {
        await signOut(auth);
      }
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setPassword('');
    } catch (err) {
      setLoginError('Sign-in failed. Check email and password.');
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      /* ignore */
    }
    setEmail('');
    setPassword('');
    setGate('login');
  };

  const handleSwitchToStaffLogin = async () => {
    try {
      await signOut(auth);
    } catch (e) {
      /* ignore */
    }
    setGate('login');
  };

  if (gate === 'loading') {
    return <div className="container py-5 text-center text-muted">Loading…</div>;
  }

  if (gate === 'denied') {
    return (
      <div className="container py-5 text-center">
        <h1 className="h4 mb-3">Staff access required</h1>
        <p className="text-muted">
          This page needs a staff account with <strong>Live scoreboard</strong>,{' '}
          <strong>Game history</strong>, or <strong>Gladiators TV</strong> permission.
        </p>
        {signedInAs && (
          <p className="small text-warning mb-3">
            You are currently signed in as <strong>{signedInAs}</strong>, which is not a stats staff
            account (for example a parent login). Sign in with your coach/admin email instead.
          </p>
        )}
        <div className="d-flex flex-wrap gap-2 justify-content-center">
          <button type="button" className="btn btn-teal" onClick={handleSwitchToStaffLogin}>
            Sign in with staff account
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setGate('login')}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  if (gate === 'login') {
    return (
      <div className="container py-5 text-center">
        <h1 className="h4 mb-3">Coach / stats access</h1>
        <p className="text-muted small mb-3">
          Sign in with your staff email (same as Admin Portal). You need Live scoreboard, Game history, or Gladiators
          TV access.
        </p>
        <form onSubmit={handleSubmit} style={{ maxWidth: 320, margin: '0 auto' }}>
          <input
            type="email"
            className="form-control mb-2"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
          />
          <input
            type="password"
            className="form-control mb-2"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          {loginError && <p className="text-danger small">{loginError}</p>}
          <button type="submit" className="btn btn-teal w-100">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-3 py-2 bg-dark text-light border-bottom border-secondary">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="small text-white-50 me-1">Roster &amp; games</span>
          <div className="btn-group" role="group" aria-label="Team age group">
            {TEAM_STATS_TEAMS.map(({ label, teamId }) => (
              <button
                key={teamId}
                type="button"
                className={`btn btn-sm ${activeTeamId === teamId ? 'btn-teal' : 'btn-outline-light'}`}
                onClick={() => setTeamAndPersist(teamId)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <button type="button" className="btn btn-sm btn-danger" onClick={handleLogout}>
          Log out
        </button>
      </div>
      <HockeyStatTrackerInner {...props} teamId={activeTeamId} />
    </div>
  );
}
